import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import {
  ShieldCheck, Receipt, Boxes, ScanLine, Store, BarChart3, WifiOff,
  Users, MessageCircle, Check, Sparkles, ArrowRight,
} from "lucide-react";

const FEATURES = [
  { icon: Receipt, title: "GST Billing", desc: "Fast, GST-ready invoices with print & thermal support." },
  { icon: Boxes, title: "Inventory & Stock", desc: "Live stock, low-stock alerts, purchase & batch tracking." },
  { icon: ScanLine, title: "Barcode Scan", desc: "Scan-to-bill, generate & print product barcodes." },
  { icon: Store, title: "Multi-Store", desc: "Manage many outlets & counters from one dashboard." },
  { icon: BarChart3, title: "Reports & Analytics", desc: "Daily sales, profit, top items, cashbook — all in one place." },
  { icon: WifiOff, title: "Offline POS", desc: "Keep billing even when internet drops; auto-syncs later." },
  { icon: Users, title: "Customer CRM", desc: "Customer ledger, dues, loyalty points & win-back offers." },
  { icon: MessageCircle, title: "WhatsApp Bills", desc: "Send bills & payment reminders on WhatsApp/SMS." },
];

const PLANS = [
  {
    id: "trial", name: "7-Day Free Trial", price: "₹0", period: "/7 days", highlight: false,
    tagline: "Bina payment ke — poora POS try karo",
    features: ["Poora billing + inventory", "GST bill & barcode", "Sab features unlocked", "7 din baad plan chuno"],
    cta: "Start Free Trial",
  },
  {
    id: "half", name: "6 Months", price: "₹180", period: " + GST", highlight: false,
    tagline: "180 din validity — ₹1/din",
    features: ["180 days validity", "Unlimited billing", "Inventory + reports", "WhatsApp bills"],
    cta: "Choose 6 Months",
  },
  {
    id: "year", name: "1 Year", price: "₹365", period: " + GST", highlight: true,
    tagline: "365 din — sabse value (₹1/din)",
    features: ["365 days validity", "Sab features included", "Offline POS", "Priority support"],
    cta: "Choose 1 Year",
  },
];

export default function RsMartPos() {
  const nav = useNavigate();
  const { user } = useAuth();
  const start = async (planId) => {
    if (!user) { toast.info("Pehle login/signup karo — phir POS activate hoga"); nav("/login?next=/rs-mart-pos&mode=register"); return; }
    if (planId !== "trial") {
      // Paid plans go through the wallet subscription screen (no free self-grant of long premium).
      nav("/seller/pos");
      return;
    }
    try {
      const { data } = await api.post("/seller-pos/subscribe/free", { trial: true });
      toast.success(data.owner_exempt ? "Aapke paas already full POS access hai" : `Free trial activate! ${data.days_left} din valid.`);
      nav("/seller/pos");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="bg-slate-50" data-testid="rs-mart-pos-page">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg,#0a1f4d 0%,#0b3d91 60%,#0a2f6e 100%)" }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-amber-300 bg-white/10 border border-white/15 px-3 py-1 rounded-full">
            <Sparkles size={13} /> Premium Business Software
          </span>
          <h1 className="text-white font-extrabold mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Rs. MART <span className="bg-blue-500 text-white px-2 rounded-lg">POS</span>
            <span className="block mt-2 text-2xl sm:text-3xl font-bold text-blue-100">Billing + Inventory ERP for your business</span>
          </h1>
          <p className="text-blue-100/90 mt-5 max-w-2xl mx-auto text-base sm:text-lg">
            Koi bhi dukaan ya business subscription le kar apna billing, inventory aur reports —
            sab kuch ek jagah manage kare. GST bill, barcode, offline POS aur WhatsApp bills, sab included.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button data-testid="hero-start-btn" onClick={() => start("trial")}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-blue-900 font-extrabold hover:scale-[1.03] transition-transform shadow-lg">
              Start Free Trial <ArrowRight size={18} />
            </button>
            <button data-testid="hero-plans-btn" onClick={() => document.getElementById("plans")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white font-bold hover:bg-white/10 transition-colors">
              View Plans
            </button>
          </div>
          <p className="text-blue-200/70 text-xs mt-4 flex items-center justify-center gap-1.5"><ShieldCheck size={13} /> No credit card needed · Cancel anytime</p>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-slate-900">Sab kuch jo dukaan chalane ke liye chahiye</h2>
        <p className="text-center text-slate-500 mt-2 text-sm sm:text-base">Ek hi app me — billing se lekar reports tak.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group bg-white rounded-2xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-lg transition-all">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                <f.icon size={22} />
              </div>
              <h3 className="font-bold text-slate-900 mt-4">{f.title}</h3>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="bg-white border-y border-slate-200 py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-slate-900">Simple, transparent pricing</h2>
          <p className="text-center text-slate-500 mt-2 text-sm sm:text-base">Apne business ke hisaab se plan chuno. Kabhi bhi upgrade/downgrade.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10 items-stretch">
            {PLANS.map((p) => (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className={`relative rounded-3xl p-6 flex flex-col ${p.highlight ? "bg-blue-900 text-white border-2 border-blue-500 shadow-2xl md:-translate-y-3" : "bg-slate-50 border border-slate-200"}`}>
                {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-black uppercase tracking-wider bg-amber-400 text-blue-950 px-3 py-1 rounded-full">Most Popular</span>}
                <h3 className={`font-extrabold text-lg ${p.highlight ? "text-white" : "text-slate-900"}`}>{p.name}</h3>
                <p className={`text-xs mt-1 ${p.highlight ? "text-blue-200" : "text-slate-500"}`}>{p.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className={`text-4xl font-extrabold ${p.highlight ? "text-white" : "text-slate-900"}`}>{p.price}</span>
                  <span className={`text-sm mb-1 ${p.highlight ? "text-blue-200" : "text-slate-400"}`}>{p.period}</span>
                </div>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.features.map((ft) => (
                    <li key={ft} className={`flex items-start gap-2 text-sm ${p.highlight ? "text-blue-50" : "text-slate-600"}`}>
                      <Check size={16} className={p.highlight ? "text-amber-300 mt-0.5 shrink-0" : "text-blue-600 mt-0.5 shrink-0"} /> {ft}
                    </li>
                  ))}
                </ul>
                <button data-testid={`plan-cta-${p.id}`} onClick={() => start(p.id)}
                  className={`mt-6 w-full py-3 rounded-xl font-extrabold transition-all ${p.highlight ? "bg-white text-blue-900 hover:scale-[1.02]" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">Rate: ₹1/din + GST · custom validity 730 din tak · 7-din free trial ke baad wallet se plan activate hota hai.</p>
        </div>
      </section>

      {/* CTA footer */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 text-center">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Aaj hi apna business digital karo</h2>
        <p className="text-slate-500 mt-2 text-sm sm:text-base">Rs. MART POS ke saath billing, stock aur growth — sab aasaan.</p>
        <button data-testid="footer-start-btn" onClick={() => start("trial")}
          className="mt-6 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-blue-600 text-white font-extrabold hover:bg-blue-700 transition-colors shadow-lg">
          Get Started Free <ArrowRight size={18} />
        </button>
      </section>
    </div>
  );
}
